import FadedLineHeader from '../../../common/components/fadedLineHeader/FadedLineHeader'
import { Button, Link, Bold, Title, LoadingIcon } from '@panwds/react-ui'
import { Form, SubmitButton, Input, Checkbox, Select } from '@panwds/react-form'
import { differenceWith } from 'lodash'
import { useFormContext } from 'react-hook-form'
import { BUTTON_CANCEL } from '../../../common/constants/LabelConstants'
import './FreeAndThirdPartyAppActivation.scss'
import { useEffect, useState, useMemo, useContext, useCallback } from 'react'
import { getPlatformRegionItems, isNonemptyArray, getTSGInstancesBasedSupportAccount } from '../../../common/utils/Utils'
import AppContext from '../../../common/context/AppContext'
const Footer = ({ onCancel, error, loading }) => {

  const { getValues } = useFormContext()
  const [agree, setAgree] = useState(false)
  const { support_account, region } = getValues()
  const isValid = agree && Boolean(support_account?.value) && Boolean(region?.value)

  return (
    <div className='tw-flex tw-flex-row tw-gap-3 tw-mt-6 footer tw-p-0'>
      <div className='tw-flex-grow tw-m-auto'>
        <div className='tw-flex tw-flex-row tw-mt-2 agree'>
          <Checkbox
            label='agree'
            name='agree'
            defaultValue={agree}
            addClassName={'tw-m-auto agree'}
            showLabel={false}
            register={{
              onChange: (e) => {
                setAgree(!agree)
              }
            }}
          />
          <span className='tw-mr-1 tw-mt-1'> Agree to the </span>
          <Link
            href='https://www.paloaltonetworks.com/content/dam/pan/en_US/assets/pdf/legal/palo-alto-networks-end-user-license-agreement-eula.pdf'
            addClassName={'tw-inline-block tw-mt-1'}
          >Terms and Conditions</Link>
        </div>
      </div>
      <Button appearance='secondary' onClick={onCancel} addClassName={'tw-m-auto'}>{BUTTON_CANCEL}</Button>
      <SubmitButton disabled={!isValid || error || loading} appearance='primary' addClassName={'tw-m-auto'}>Activate Now</SubmitButton>
    </div>
  )
}
const AppAssociations = ({ appAssociatedInstances, getAppName, appName }) => {
  const { apps } = useContext(AppContext)

  const associationsHelperText = 'Activating this service, for this tenant, will automatically associate it with the following applications:'

  return <div>
    <p className='tw-block-inline tw-mb-0'>Associations</p>
    <p className='helper-text'>{associationsHelperText}</p>
    {appAssociatedInstances.map((each) => (
      <div key={each.app_id} className={'tw-text-s tw-mb-1'}>{`${getAppName(each.app_id)} (${apps[each.app_id]?.regions?.find(region => region.name === each.region)?.display})`}</div>
    ))}
  </div>
}

const FormItems = ({ supportAccountIds, apps, app, instances }) => {
  const { setValue } = useFormContext()
  const [selectedCSP, setSelectedCSP] = useState(null)

  const regions = useMemo(() => {
    const platformRegions = getPlatformRegionItems(app.app_id, apps, instances, selectedCSP?.id) // do not support fed regions
    return platformRegions.length ? platformRegions : [{ value: '' }]
  }, [instances, app, apps, selectedCSP])

  const onCSPChange = useCallback((event) => {
    setSelectedCSP(event.target.value)
    setValue('region', null)
  }, [setSelectedCSP, setValue])

  return (
    <div>
      <div className='tw-grid tw-grid-cols-2 tw-gap-4 tw-w-4/5'>
        <Select
          addClassName='cmm-modal-select'
          fullWidth
          fitToItemWidth
          label='Customer Support Account'
          name='support_account'
          items={supportAccountIds}
          placeholder={'Please select a CSP'}
          defaultValue={supportAccountIds.length === 1 ? supportAccountIds[0] : { value: '' }}
          register={{
            required: true,
            onChange: onCSPChange
          }}

        />
        <div className='tw-mt-6 helper-text'>Choose the account you would like to attach this license to.</div>
        <Select
          fullWidth
          fitToItemWidth
          label='Region'
          name='region'
          items={regions}
          defaultValue={regions.length === 1 ? regions[0] : { value: '' }}
          placeholder={'Please select a region'}
          addClassName={'cmm-modal-select app_region'}
          register={{
            required: true
          }}
        />
        <div className='tw-mt-6 helper-text'>Choose the region closest to where the data will be written and stored.</div>
      </div>
      <div className='tw-w-2/5 tw-mt-6'>
        {app?.developer_defined_fields && Array.isArray(app?.developer_defined_fields) &&
          app.developer_defined_fields.map(field => (
            <Input
              name={field.name}
              label={field.display}
              key={field.name}
            />
          ))
        }
      </div>
    </div>
  )
}

const FreeAndThirdPartyAppActivation = (props) => {
  const { app, supportAccountIds, instances, onCancel, onSubmit, apps, loading } = props
  const [error, setError] = useState(false)
  const [defaultCSP, setDefaultCSP] = useState(undefined)
  const { getAppName } = useContext(AppContext)

  useEffect(() => {
    if (instances.some(instance => instance.app_id_display_override === app.app_id || instance.app_id === app.app_id)) {
      setError(`${app.display_name} cannot be activated. instance already exists in the tenant.`)
    }
    else if (instances.some(instance => instance.app_id === app?.setup?.override?.app_id)) { // remove once buckbeak moves to paid activation
      const override_app_display = apps[app?.setup?.override?.app_id]?.display_name
      setError(`${app.display_name} cannot be activated. ${override_app_display} instance already exists in the tenant.`)
    }
    else {
      const requiredAssociations = app?.associations?.reduce((result, each) => {
        if (each?.required) {
          result.push(each.app_id)
        }
        return result
      }, [])

      const instanceList = instances?.map(instance => instance.app_id)
      const missingAssociations = differenceWith(requiredAssociations, instanceList)?.map(app_id => getAppName(app_id))
      if (isNonemptyArray(missingAssociations)) {
        setError(`This application cannot be activated because required association(s) "${missingAssociations?.join(',')}" not activated yet!`)
      }
    }
  }, [instances, app, apps, setError, getAppName])

  const filteredAndSortedSupportAccounts = useMemo(() => {
    let supportAccounts = [{ value: '' }]
    const filteredSupportAccounts = supportAccountIds?.filter(each => each.supportAccountCategory[0].categoryId === 1)
    try {
      supportAccounts = getTSGInstancesBasedSupportAccount({ tsgInstances: instances, supportAccounts: filteredSupportAccounts, app })
    }
    catch (e) {
      if (!error) {
        setError(e.message)
      }
    }
    return supportAccounts
  }, [app, supportAccountIds, instances, error])

  useEffect(() => {
    const csp = filteredAndSortedSupportAccounts.length > 1 ? undefined : Boolean(filteredAndSortedSupportAccounts[0].value) ? filteredAndSortedSupportAccounts[0] : undefined
    setDefaultCSP(csp)
  }, [filteredAndSortedSupportAccounts])

  const appAssociatedInstances = useMemo(() => {
    const associatedInstances = app?.associations?.reduce((result, association) => {
      const associatedInstance = instances.find(instance => instance.app_id === association.app_id && +instance.support_account_id === defaultCSP?.id)
      if (associatedInstance) {
        result.push(associatedInstance)
      }
      return result
    }, [])
    return associatedInstances
  }, [app, instances, defaultCSP])

  const appAssociatingWithInstances = useMemo(() => {
    const onboardingAppId = app.app_id
    const associatingInstances = instances?.reduce((result, instance) => {
      const instance_app = apps[instance.app_id]
      if (instance_app?.associations?.find(assoc => assoc.app_id === onboardingAppId && !instance?.associations?.find(assoc => assoc.app_id === onboardingAppId))) {
        result.push(instance)
      }
      return result
    }, [])
    return associatingInstances
  }, [app, apps, instances])

  const onFormSubmit = (formValues) => {
    if (!formValues.agree) {
      setError('Please accept terms and conditions!')
    }
    else {
      setError('')
      onSubmit(formValues)
    }
  }

  return (
    loading ?
      <div
        className="tw-flex tw-absolute tw-inset-0 tw-justify-center tw-items-center tw-z-50">
        <LoadingIcon size="4xl"/>
      </div> :
      <div className='tw-h-full tw-overflow-y-auto'>
        <FadedLineHeader title='ACTIVATE SERVICES IN THIS TENANT' position='left' />
        <div className='tw-border-green tw-border-solid tw-border-2 tw-rounded tw-m-4'>
          <Title level={2} size={'xxs'} addClassName='tw-mt-4 tw-mb-6 tw-pl-4 tw-pr-4'>{`Activate ${app.display_name}`}</Title>
          {error && <div className='tw-mb-4 tw-pl-4 tw-pr-4'>
            <Bold appearance='semibold' addClassName='tw-text-error'>{error}</Bold>
          </div>}
          <Form onSubmit={onFormSubmit} mode='onBlur' formProps={{ style: { height: '100%', width: '100%', position: 'relative' } }}>
            <div className='tw-pl-4 tw-pr-4'>
              <FormItems supportAccountIds={filteredAndSortedSupportAccounts} {...{ apps }} app={app} isThirdParty={app?.is_third_party} {...{ instances }} />
              {Boolean(appAssociatedInstances.length) && <AppAssociations appAssociatedInstances={appAssociatedInstances} getAppName={getAppName} appName={app.display_name} />}
              {Boolean(appAssociatingWithInstances.length) && <AppAssociations appAssociatedInstances={appAssociatingWithInstances} getAppName={getAppName} appName={app.display_name} />}
            </div>
            <Footer onCancel={onCancel} {...{ error }} {...{ loading }} />
          </Form>
        </div>
      </div>
  )
}

FreeAndThirdPartyAppActivation.propTypes = {}
export default FreeAndThirdPartyAppActivation
